import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createAppointment } from '../../redux/slices/appointmentsSlice';
import { 
  fetchPatients, 
  selectPatients, 
  selectPatientsLoading, 
  selectPatientsError 
} from '../../redux/slices/patientsSlice';
import { 
  selectUserFacilities, 
} from '../../redux/slices/facilitiesSlice';
import { useUserData } from '../../hooks/useUserData';
import { 
  TextField, 
  Button, 
  Box, 
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { APPOINTMENT_TYPES } from '../../types/enums';
import PropTypes from 'prop-types';
import { useFacilityChangeListener } from '../../hooks/useFacilityChangeListener';
import StaffTimelineView from './StaffTimelineView';
import AddIcon from '@mui/icons-material/Add';

// Styled Components - Purple/Blue Gradient Header
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(1.5),
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
}));

/**
 * CreateAppointment Component
 * 
 * Creates a new appointment for either an existing or new patient.
 * The facility is always locked to the logged-in user's default facility
 * and cannot be changed during appointment creation.
 */
const CreateAppointment = ({
  mode = 'staff',
  allowedAppointmentTypes,
  preselectedFacilityId,
  hidePatientSelector = false,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Use the new useUserData hook for consistent data structure
  const { defaultFacility } = useUserData();
  
  const patients = useSelector(selectPatients);
  const patientsLoading = useSelector(selectPatientsLoading);
  const patientsError = useSelector(selectPatientsError);
  const userFacilities = useSelector(selectUserFacilities);
  const loading = useSelector(state => state.appointments.loading);
  const [fetchError, setFetchError] = useState('');
  const [error, setError] = useState('');
  const [patientMode, setPatientMode] = useState('existing');
  const [newPatientFields, setNewPatientFields] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    driversLicense: '',
    phone: '',
    email: '',
  });

  const [formData, setFormData] = useState({
    patientId: '',
    facilityId: '',
    date: new Date(),
    appointmentType: '',
    phone: '',
    email: '',
    communicationMethod: '',
    purpose: '',
    status: 'scheduled',
    notes: ''
  });

  const getDefaultFacilityId = useCallback(() => {
    // Always prioritize the user's default facility
    if (defaultFacility && defaultFacility.id) {
      return defaultFacility.id;
    }
    // Fallback to preselected facility if no default facility
    if (preselectedFacilityId && preselectedFacilityId !== '') {
      return preselectedFacilityId;
    }
    // Last resort: first available user facility
    if (userFacilities && userFacilities.length > 0) {
      return userFacilities[0].id;
    }
    return '';
  }, [defaultFacility, preselectedFacilityId, userFacilities]);

  useEffect(() => {
    const defaultFacilityId = getDefaultFacilityId();
    if (defaultFacilityId) {
      dispatch(fetchPatients(defaultFacilityId))
        .unwrap()
        .catch(error => {
          console.error('Error fetching patients:', error);
          setFetchError('Failed to load patients. Please refresh the page.');
        });
    }
  }, [dispatch, getDefaultFacilityId]);

  // Initialize facility ID when component mounts or dependencies change
  // Always set to user's default facility for appointment creation
  useEffect(() => {
    const defaultFacilityId = getDefaultFacilityId();
    if (defaultFacilityId && defaultFacilityId !== '') {
      setFormData(prev => ({ ...prev, facilityId: defaultFacilityId }));
    }
  }, [getDefaultFacilityId]);

  // Watch for changes in defaultFacility and update form data
  useEffect(() => {
    console.log('CreateAppointment: defaultFacility changed:', defaultFacility);
    if (defaultFacility && defaultFacility.id) {
      console.log('CreateAppointment: Updating form data with new facility:', defaultFacility.id);
      setFormData(prev => ({ ...prev, facilityId: defaultFacility.id }));
      // Also refresh patients for the new facility
      dispatch(fetchPatients(defaultFacility.id))
        .unwrap()
        .catch(error => {
          console.error('Error fetching patients after default facility change:', error);
          setFetchError('Failed to load patients. Please refresh the page.');
        });
    }
  }, [defaultFacility, dispatch]);

  useEffect(() => {
    if (patientMode === 'existing' && formData.patientId) {
      const selectedPatient = patients.find((p) => p.id === formData.patientId);
      if (selectedPatient) {
        setNewPatientFields({
          firstName: selectedPatient.firstName || '',
          lastName: selectedPatient.lastName || '',
          dob: selectedPatient.dob || '',
          driversLicense: selectedPatient.driversLicense || '',
          phone: selectedPatient.phone || '',
          email: selectedPatient.email || '',
        });
      }
    }
  }, [formData.patientId, patientMode, patients]);

  const communicationMethods = [
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'both', label: 'Both' },
    { value: 'none', label: 'None' },
  ];

  const handleDateChange = (newDate) => {
    setFormData(prev => ({ ...prev, date: newDate }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePatientModeChange = (e) => {
    setPatientMode(e.target.value);
    if (e.target.value === 'existing') {
      setNewPatientFields({
        firstName: '',
        lastName: '',
        dob: '',
        driversLicense: '',
        phone: '',
        email: '',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (!formData.facilityId) {
        setError('Please select a facility');
        return;
      }

      const selectedPatient = patients.find(p => p.id === formData.patientId);
      const selectedFacility = userFacilities.find(f => f.id === formData.facilityId);

      const appointmentData = {
        ...formData,
        patientName: selectedPatient?.name || '',
        facilityName: selectedFacility.name,
        date: formData.date.toISOString(),
        status: 'scheduled',
        ...newPatientFields
      };

      if (patientMode === 'new') {
        // If patientMode is 'new', do not set patientId
      } else {
        appointmentData.patientId = selectedPatient.id;
      }

      const result = await dispatch(createAppointment(appointmentData)).unwrap();
      navigate('/appointments');
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      setError('Failed to create appointment. Please try again.');
      console.error('Error creating appointment:', err);
    }
  };

  // Remove duplicate patients by ID and sort by name
  const uniquePatients = Array.from(
    new Map(patients.map(patient => [patient.id, patient])).values()
  ).sort((a, b) => {
    const nameA = a.name || '';
    const nameB = b.name || '';
    return nameA.localeCompare(nameB);
  });

  const selectedFacilityObj = userFacilities.find(f => f.id === formData.facilityId);
  const isEmergency = selectedFacilityObj?.encounterType === 'emergency';
  const appointmentTypeOptions = APPOINTMENT_TYPES.filter(type => {
    if (isEmergency) {
      return type === 'emergency' || type === 'telehealth';
    }
    return true;
  });
  

  // Listen for facility changes and refresh facility data
  const handleFacilityChange = (newFacilityId, previousFacilityId) => {
    console.log('CreateAppointment: Facility changed, refreshing facility data', {
      newFacilityId,
      previousFacilityId,
      currentFormFacilityId: formData.facilityId
    });
    // Update form data if the facility was changed
    if (formData.facilityId === previousFacilityId) {
      console.log('CreateAppointment: Updating form data facility ID from', previousFacilityId, 'to', newFacilityId);
      setFormData(prev => ({ ...prev, facilityId: newFacilityId }));
    } else {
      console.log('CreateAppointment: Form facility ID does not match previous facility ID, not updating');
    }
    // Always refresh patients when facility changes
    if (newFacilityId) {
      console.log('CreateAppointment: Fetching patients for new facility:', newFacilityId);
      dispatch(fetchPatients(newFacilityId))
        .unwrap()
        .catch(error => {
          console.error('Error fetching patients after facility change:', error);
          setFetchError('Failed to load patients. Please refresh the page.');
        });
    }
  };

  useFacilityChangeListener(handleFacilityChange);

  return (
    <Box sx={{ maxWidth: 1600, margin: '0 auto', p: 2 }}>
      {/* Purple/Blue Gradient Header */}
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                <AddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                New Appointment
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Create a new appointment for a patient
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            {(error || fetchError || patientsError) && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error || fetchError || patientsError}
              </Typography>
            )}
            <form onSubmit={handleSubmit}>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  value={patientMode}
                  onChange={handlePatientModeChange}
                  name="patientMode"
                >
                  <FormControlLabel value="existing" control={<Radio size="small" />} label="Existing Patient" />
                  <FormControlLabel value="new" control={<Radio size="small" />} label="New Patient" />
                </RadioGroup>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }} size="small" margin="dense">
                <InputLabel id="facility-select-label">Facility</InputLabel>
                <Select
                  labelId="facility-select-label"
                  id="facility-select"
                  name="facilityId"
                  value={formData.facilityId}
                  disabled
                  required
                  label="Facility"
                  inputProps={{
                    'aria-hidden': false,
                    tabIndex: 0
                  }}
                >
                  {defaultFacility ? (
                    <MenuItem value={defaultFacility.id}>
                      {defaultFacility.name}
                    </MenuItem>
                  ) : (
                    <MenuItem disabled>No default facility available</MenuItem>
                  )}
                </Select>
              </FormControl>

              {patientMode === 'existing' && mode === 'staff' && !hidePatientSelector && (
                <FormControl fullWidth sx={{ mb: 2 }} size="small" margin="dense">
                  <InputLabel id="patient-select-label">Patient</InputLabel>
                  <Select
                    labelId="patient-select-label"
                    id="patient-select"
                    name="patientId"
                    value={formData.patientId}
                    onChange={handleInputChange}
                    required
                    label="Patient"
                    disabled={patientsLoading}
                    inputProps={{
                      'aria-hidden': false,
                      tabIndex: 0
                    }}
                  >
                    {patientsLoading ? (
                      <MenuItem disabled>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading patients...
                        </Box>
                      </MenuItem>
                    ) : uniquePatients.length > 0 ? (
                      uniquePatients.map((patient) => (
                        <MenuItem key={patient.id} value={patient.id}>
                          {patient.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No patients available</MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                margin="dense"
                size="small"
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                margin="dense"
                size="small"
                label="Email (optional)"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }} size="small" margin="dense">
                <InputLabel id="communication-method-label">Communication Method</InputLabel>
                <Select
                  labelId="communication-method-label"
                  id="communication-method"
                  name="communicationMethod"
                  value={formData.communicationMethod}
                  onChange={handleInputChange}
                  required
                  label="Communication Method"
                  inputProps={{
                    'aria-hidden': false,
                    tabIndex: 0
                  }}
                >
                  {communicationMethods.map((method) => (
                    <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }} size="small" margin="dense">
                <InputLabel id="appointment-type-label">Appointment Type</InputLabel>
                <Select
                  labelId="appointment-type-label"
                  id="appointment-type"
                  name="appointmentType"
                  value={formData.appointmentType}
                  onChange={handleInputChange}
                  required
                  label="Appointment Type"
                  inputProps={{
                    'aria-hidden': false,
                    tabIndex: 0
                  }}
                >
                  {appointmentTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }} size="small" margin="dense">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Appointment Date & Time"
                    value={formData.date}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true,
                        size: 'small',
                        margin: 'dense',
                      }
                    }}
                  />
                </LocalizationProvider>
              </FormControl>

              <TextField
                fullWidth
                margin="dense"
                size="small"
                label="Purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                margin="dense"
                size="small"
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />

              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading || patientsLoading}
                  size="small"
                >
                  {loading ? 'Creating...' : 'Create Appointment'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/appointments')}
                  size="small"
                >
                  Cancel
                </Button>
              </Box>
            </form>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            {(() => {
              const selectedFacility = (formData.facilityId && formData.facilityId !== '') || 
                (preselectedFacilityId && preselectedFacilityId !== '') 
                  ? (formData.facilityId || preselectedFacilityId) 
                  : null;
              return (
                <StaffTimelineView selectedFacility={selectedFacility} />
              );
            })()}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

CreateAppointment.propTypes = {
  mode: PropTypes.oneOf(['staff', 'patient']),
  allowedAppointmentTypes: PropTypes.arrayOf(PropTypes.string),
  preselectedFacilityId: PropTypes.string,
  hidePatientSelector: PropTypes.bool,
  onSuccess: PropTypes.func,
};

export default CreateAppointment;
